import React, { useEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { ChevronsRightIcon, CheckIcon } from 'lucide-react';
interface SlideToConfirmProps {
  label: string;
  confirmedLabel: string;
  onConfirm: () => void;
  size?: 'sm' | 'wall';
}
const PAD = 4;

/** Approval control for Niveau 3. Keyboard: arrow right in steps, arrow left / Esc resets. */
export function SlideToConfirm({
  label,
  confirmedLabel,
  onConfirm,
  size = 'sm'
}: SlideToConfirmProps) {
  const HANDLE = size === 'wall' ? 60 : 48;
  const trackRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(280);
  const [done, setDone] = useState(false);
  const x = useMotionValue(0);
  const max = Math.max(1, width - HANDLE - PAD * 2);
  const labelOpacity = useTransform(x, [0, max * 0.6], [1, 0]);
  const fillWidth = useTransform(x, v => v + HANDLE + PAD);
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => x.on('change', v => setPct(Math.round(v / max * 100))), [x, max]);
  const confirm = () => {
    animate(x, max, {
      duration: 0.15,
      ease: [0.23, 1, 0.32, 1]
    });
    setDone(true);
    onConfirm();
  };
  const reset = () => animate(x, 0, {
    type: 'spring',
    stiffness: 500,
    damping: 35
  });
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (done) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = Math.min(max, x.get() + max / 4);
      x.set(next);
      if (next >= max) confirm();
    } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
      e.preventDefault();
      reset();
    }
  };
  const height = HANDLE + PAD * 2;
  const textSize = size === 'wall' ? 'text-[22px]' : 'text-[15px]';
  if (done) {
    return <div style={{
      height
    }} className={`flex items-center justify-center gap-2 rounded-full bg-accent/15 font-semibold text-accent ${textSize}`} data-id="element-1371" data-mp="2gttcpvrjch60">
        <CheckIcon className="h-5 w-5" aria-hidden data-id="element-1372" data-mp="or7dwce57az8" />
        {confirmedLabel}
      </div>;
  }
  return <div ref={trackRef} style={{
    height
  }} className="relative select-none overflow-hidden rounded-full border border-white/10 bg-white/[0.05]" data-id="element-1373" data-mp="tjqt1dshae65">
      <motion.div aria-hidden className="absolute inset-y-0 left-0 rounded-full bg-accent/20" style={{
      width: fillWidth
    }} data-id="element-1374" data-mp="vct2shtx7ry" />
      <motion.span aria-hidden style={{
      opacity: labelOpacity,
      paddingLeft: HANDLE
    }} className={`pointer-events-none absolute inset-0 flex items-center justify-center font-medium text-ink-soft ${textSize}`} data-id="element-1375" data-mp="5ufqbqrki9pd">
        {label}
      </motion.span>
      <motion.button type="button" role="slider" aria-label={`${label}. Gebruik pijl rechts om te schuiven.`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} drag="x" dragConstraints={{
      left: 0,
      right: max
    }} dragElastic={0} dragMomentum={false} onDragEnd={() => x.get() > max * 0.85 ? confirm() : reset()} onKeyDown={onKeyDown} style={{
      x,
      width: HANDLE,
      height: HANDLE,
      top: PAD - 1,
      left: PAD - 1
    }} className="absolute grid cursor-grab touch-none place-items-center rounded-full bg-accent text-night-950 active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink" data-id="element-1376" data-mp="1m8pas9soveax">
        <ChevronsRightIcon className={size === 'wall' ? 'h-6 w-6' : 'h-5 w-5'} strokeWidth={2.25} aria-hidden data-id="element-1377" data-mp="2ny5njwummkk0" />
      </motion.button>
    </div>;
}